import { get, noop } from 'lodash'
import React, { useMemo } from 'react'
import { Popup as SemanticPopup, StrictPopupProps } from 'semantic-ui-react'

/**
 * Abstract wrapper for semantic ui popup. Use components/TooltipTrigger or components/PopoverTrigger.
 * @param {*} props
 */
export type TPopupPosition =
  | 'top left'
  | 'top right'
  | 'bottom right'
  | 'bottom left'
  | 'right center'
  | 'left center'
  | 'top center'
  | 'bottom center'

export type TPlacement =
  | 'auto'
  | 'top'
  | 'left'
  | 'right'
  | 'bottom'
  | TPopupPosition

export interface IPopup extends StrictPopupProps {
  placement?: TPlacement
  location?: TPlacement
}

function isReactFragment(v): boolean {
  if (v && v.type) {
    return v.type === React.Fragment
  }
  return v === React.Fragment
}

const Popup = (props: IPopup): React.ReactElement => {
  const {
    position = 'auto',
    placement = 'auto',
    location = 'auto',
    on = 'hover',
    offset,
    as,
    basic,
    children,
    className,
    content,
    context,
    disabled,
    eventsEnabled,
    flowing,
    header,
    hideOnScroll,
    hoverable,
    inverted,
    onClose = noop,
    onMount = noop,
    onOpen = noop,
    onUnmount = noop,
    pinned,
    positionFixed,
    popper,
    popperDependencies,
    size,
    style,
    wide,
    ...rest
  } = props

  const pos = useMemo(() => {
    const value = (position ||
      placement ||
      location ||
      'top center') as TPlacement

    if (value === 'top' || value === 'auto') {
      return 'top center'
    }
    if (value === 'left') {
      return 'left center'
    }
    if (value === 'right') {
      return 'right center'
    }
    if (value === 'bottom') {
      return 'bottom center'
    }
    return value
  }, [position, placement, location])

  let trigger = props.trigger
  if (isReactFragment(trigger)) {
    trigger = <div>{get(trigger, 'props.children')}</div>
  }

  const modifiers = [
    { name: 'preventOverflow', enabled: true },
    offset
      ? {
          name: 'offset',
          options: {
            offset,
          },
        }
      : undefined,
  ].filter(Boolean)

  return (
    <SemanticPopup
      {...rest}
      as={as}
      basic={basic}
      className={className}
      content={content}
      context={context}
      disabled={disabled}
      eventsEnabled={eventsEnabled}
      flowing={flowing}
      header={header}
      hideOnScroll={hideOnScroll}
      hoverable={hoverable}
      inverted={inverted}
      onClose={onClose}
      onMount={onMount}
      onOpen={onOpen}
      onUnmount={onUnmount}
      pinned={pinned}
      positionFixed={positionFixed}
      popper={popper}
      popperDependencies={popperDependencies}
      size={size}
      style={style}
      wide={wide}
      trigger={trigger}
      position={pos}
      on={on}
      popperModifiers={modifiers}
    >
      {children}
    </SemanticPopup>
  )
}

export default Popup
